// @ts-ignore
import './ProductSidebar.scss';

import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';

// @ts-ignore
import { ReactComponent as WarningIcon } from '../../assets/images/warning-triangle-icon.svg';

import Dropdown from '../../component/input/Dropdown';
import InputNumber from '../../component/input/InputNumber';
import InputText from '../../component/input/InputText';
import InputSwitch from '../../component/input/InputSwitch';
import DetailsSidebar from '../../component/detailssidebar/DetailsSidebar';
import LoadingSpinner from '../../component/loadingoverlay/LoadingSpinner';

import { MessageContext } from '../../context/MessageContext';

import { ListParams } from '../../service/warehouse';
import WarehouseProductCategoryService from '../../service/warehouse/ProductCategory/ProductCategoryService';

// Reference endpoints still used for dropdown values, EAN checks, and photos.
import { ProductService } from '../../service/ProductService';
import { PlanogramService } from '../../service/PlanogramService';
import { ReturnablePackagingService } from '../../service/ReturnablePackagingService';

import useValidator from '../../utils/validator/useValidator';
import { useI18nUtils } from '../../utils/i18nUtils';

type ViewMode = 'EDIT' | 'NEW';
type WarehouseProduct = Record<string, any>;

interface Option {
    id?: string | boolean;
    uuid?: string;
    label: string;
    originalName?: unknown;
    price?: number | null;
    component?: boolean;

    // Category metadata used when legacy and warehouse category sources are merged.
    categorySource?: 'LEGACY' | 'WAREHOUSE' | 'MERGED';
    categoryType?: 'PRODUCT' | 'COMPONENT';
    legacyCategoryId?: string;
    warehouseCategoryId?: string;
}

interface ProductForm {
    systemId: string;
    externalSystemId: string;

    productWithoutEan: boolean;
    ean: string;

    name: string;
    useAsComponent: boolean;
    category: Option | null;
    includeComponents: boolean;

    // Figma fields not currently represented by the warehouse request contract.
    weightVolume: number | null;
    unitOfPackage: Option | null;
    price: number | null;
    currency: Option | null;
    returnablePackagingEnabled: boolean;
    returnablePackaging: Option | null;
    returnablePackagingPrice: number | null;
    masterPackEan: string;

    unitOfMeasurement: Option | null;
    vatCode: Option | null;
    minQuantity: number | null;
    maxQuantity: number | null;

    // Warehouse `unitForPackage` is displayed in Figma as "Units per master pack".
    unitsPerMasterPack: number | null;

    status: Option | null;
}

interface ProductSidebarProps {
    visible: boolean;
    onHide?: () => void;
    onSuccess?: (data?: unknown) => void;
    viewMode: ViewMode;
    externalId?: string | null;
    initialName?: string | null;
    initialEanCode?: string | null;
    initialProductWithoutEan?: boolean;
    initialUseAsComponent?: boolean;
    onStatusChangedToInactive?: (data: {
        externalId: string;
        productName: string;
    }) => void;
    focusName?: boolean;
    duplicatedProductData?: WarehouseProduct | null;
}

interface InventoryProductRequest {
    externalSystemId?: string;
    ean: string | null;
    name: string;
    category?: {
        uuid: string;
    };
    warehouseCategory?: {
        uuid: string;
    };
    weightVolume?: number;
    unitOfMeasurement?: string;
    minQuantity?: number;
    maxQuantity?: number;
    price?: number;
    currency?: string;
    vatCode?: string;
    status?: string;
    returnablePackaging: {
        uuid: string;
        name: unknown;
    } | null;
    useAsComponent: boolean;
    includeComponents: boolean;

    // Master-pack fields (warehouse-service PR #244).
    // omitted key  -> leave unchanged
    // "" or null   -> clear
    // a value      -> set
    unitForPackage?: string | null;
    eanMasterPack?: string | null;
    unitsPerMasterPack?: number | null;
}

const DEFAULT_CURRENCY = 'PLN';

const DEFAULT_CURRENCY_OPTION: Option = {
    id: DEFAULT_CURRENCY,
    label: DEFAULT_CURRENCY,
};

const EMPTY_FORM: ProductForm = {
    systemId: '',
    externalSystemId: '',

    productWithoutEan: false,
    ean: '',

    name: '',
    useAsComponent: false,
    category: null,
    includeComponents: false,

    weightVolume: null,
    unitOfPackage: null,
    price: null,
    currency: DEFAULT_CURRENCY_OPTION,
    returnablePackagingEnabled: false,
    returnablePackaging: null,
    returnablePackagingPrice: null,
    masterPackEan: '',

    unitOfMeasurement: null,
    vatCode: null,
    minQuantity: null,
    maxQuantity: null,
    unitsPerMasterPack: null,

    status: null,
};

const stringValue = (value: unknown): string => {
    if (value === null || value === undefined) {
        return '';
    }

    return String(value);
};

const numberOrNull = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const roundMoney = (value: number): number =>
    Math.round((value + Number.EPSILON) * 100) / 100;

const calculateBaseProductPrice = (
    storedPrice: number | null,
    returnablePackagingEnabled: boolean,
    returnablePackagingPrice: number | null,
): number | null => {
    if (storedPrice === null) {
        return null;
    }

    if (!returnablePackagingEnabled || returnablePackagingPrice === null) {
        return storedPrice;
    }

    return roundMoney(storedPrice - returnablePackagingPrice);
};

const calculateFinalProductPrice = (
    basePrice: number | null,
    returnablePackagingEnabled: boolean,
    returnablePackagingPrice: number | null,
): number | null => {
    if (basePrice === null) {
        return null;
    }

    return roundMoney(
        basePrice +
            (returnablePackagingEnabled ? (returnablePackagingPrice ?? 0) : 0),
    );
};

const optionalString = (value: string): string | undefined => {
    const normalized = value.trim();
    return normalized || undefined;
};

const booleanValue = (value: unknown, fallback = false): boolean => {
    if (value === null || value === undefined) {
        return fallback;
    }

    if (typeof value === 'string') {
        return value.toLowerCase() === 'true';
    }

    return Boolean(value);
};

const ProductSidebar: React.FC<ProductSidebarProps> = ({
    visible,
    onHide,
    onSuccess,
    viewMode,
    externalId,
    initialName,
    initialEanCode,
    initialProductWithoutEan = false,
    initialUseAsComponent = false,
    onStatusChangedToInactive,
    focusName,
    duplicatedProductData,
}) => {
    const { t } = useTranslation();
    const validator = useValidator();
    const { getI18FromName } = useI18nUtils();

    const { showErrorMessage, showSuccessMessage } = useContext(
        MessageContext,
    ) as any;

    const isMounted = useRef(false);
    const eanDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [unitOptions, setUnitOptions] = useState<Option[]>([]);
    const [statusOptions, setStatusOptions] = useState<Option[]>([]);
    const [vatCodeOptions, setVatCodeOptions] = useState<Option[]>([]);
    const [categoryOptions, setCategoryOptions] = useState<Option[]>([]);
    const [returnablePackagingOptions, setReturnablePackagingOptions] =
        useState<Option[]>([]);

    const [mainKey, setMainKey] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loadedProduct, setLoadedProduct] = useState<WarehouseProduct | null>(
        null,
    );
    const [photoUrl, setPhotoUrl] = useState<string | null>(null);
    const [isPhotoLoading, setIsPhotoLoading] = useState(false);
    const [isEanAvailable, setIsEanAvailable] = useState(true);
    const [isCheckingEan, setIsCheckingEan] = useState(false);

    const [form, setForm] = useState<ProductForm>({ ...EMPTY_FORM });

    const yesNoOptions = useMemo<Option[]>(
        () => [
            {
                id: false,
                label: t('common.no', 'No'),
            },
            {
                id: true,
                label: t('common.yes', 'Yes'),
            },
        ],
        [t],
    );

    const currencyOptions = useMemo<Option[]>(() => {
        const currentCurrency =
            typeof form.currency?.id === 'string' && form.currency.id.trim()
                ? form.currency.id
                : DEFAULT_CURRENCY;

        return [
            {
                id: currentCurrency,
                label: currentCurrency,
            },
        ];
    }, [form.currency?.id]);

    const visibleCategoryOptions = useMemo(
        () =>
            categoryOptions.filter(
                (option) =>
                    // Warehouse categories must remain available regardless
                    // of whether the product is used as a component. MERGED
                    // options also represent a Warehouse category and carry
                    // its identifier.
                    option.categorySource === 'WAREHOUSE' ||
                    option.categorySource === 'MERGED' ||
                    // Legacy categories often have no PRODUCT/COMPONENT type.
                    // Keep them available in both form modes.
                    option.component === undefined ||
                    option.component === form.useAsComponent,
            ),
        [categoryOptions, form.useAsComponent],
    );

    const selectedUseAsComponent = useMemo(
        () =>
            yesNoOptions.find((option) => option.id === form.useAsComponent) ??
            null,
        [form.useAsComponent, yesNoOptions],
    );

    const selectedIncludeComponents = useMemo(
        () =>
            yesNoOptions.find(
                (option) => option.id === form.includeComponents,
            ) ?? null,
        [form.includeComponents, yesNoOptions],
    );

    /*
     * Three product configurations (see #841 / #842):
     *
     *   isComponent                     -> useAsComponent = true
     *   isProductContainingComponents   -> useAsComponent = false, includeComponents = true
     *   isStandaloneProduct             -> useAsComponent = false, includeComponents = false
     *
     * Stock thresholds (min/max quantity) and returnable packaging are
     * standalone-only.
     *
     * Master-pack fields (unit of package, units per master pack, master
     * pack EAN) apply to standalone products AND components per the
     * corrected Figma - only "contains components" hides them.
     */

    const isProductContainingComponents =
        !form.useAsComponent && form.includeComponents;

    const isStandaloneProduct =
        !form.useAsComponent && !form.includeComponents;

    const showMasterPackFields = !isProductContainingComponents;

    /*
     * Master pack EAN is itself an EAN-style barcode value, so it doesn't
     * make sense on a product that has no EAN at all. The "Product without
     * EAN" switch hides (and clears) it in addition to the
     * contains-components gating above. Units per master pack and Unit of
     * package are not EAN-shaped and stay visible regardless of
     * productWithoutEan.
     */
    const showMasterPackEan = showMasterPackFields && !form.productWithoutEan;

    const returnablePackagingPriceOptions = useMemo<Option[]>(() => {
        const prices = new Map<number, Option>();

        returnablePackagingOptions.forEach((option) => {
            if (option.price === null || option.price === undefined) {
                return;
            }

            prices.set(option.price, {
                id: String(option.price),
                label: option.price.toFixed(2).replace('.', ','),
            });
        });

        return Array.from(prices.values());
    }, [returnablePackagingOptions]);

    const selectedReturnablePackagingPrice = useMemo(
        () =>
            returnablePackagingPriceOptions.find(
                (option) => option.id === String(form.returnablePackagingPrice),
            ) ?? null,
        [form.returnablePackagingPrice, returnablePackagingPriceOptions],
    );

    const resetForm = useCallback(() => {
        setForm({ ...EMPTY_FORM });
        setLoadedProduct(null);
        setMainKey((previous) => previous + 1);
        setPhotoUrl(null);
        setIsPhotoLoading(false);
        setIsEanAvailable(true);
        setIsCheckingEan(false);
    }, []);

    const focusNameInput = useCallback(() => {
        if (!focusName) return;

        setTimeout(() => {
            document.getElementById('text-name')?.focus();
        }, 0);
    }, [focusName]);

    const findOptionById = useCallback(
        (options: Option[], value: unknown): Option | null => {
            if (value === null || value === undefined || value === '') {
                return null;
            }

            const normalizedValue = String(value);

            return (
                options.find((option) => {
                    const optionId =
                        option.id !== undefined ? String(option.id) : undefined;
                    const optionUuid =
                        option.uuid !== undefined
                            ? String(option.uuid)
                            : undefined;
                    const legacyCategoryId =
                        option.legacyCategoryId !== undefined
                            ? String(option.legacyCategoryId)
                            : undefined;
                    const warehouseCategoryId =
                        option.warehouseCategoryId !== undefined
                            ? String(option.warehouseCategoryId)
                            : undefined;

                    return (
                        optionId === normalizedValue ||
                        optionUuid === normalizedValue ||
                        legacyCategoryId === normalizedValue ||
                        warehouseCategoryId === normalizedValue
                    );
                }) ?? null
            );
        },
        [],
    );

    const mapProductToForm = useCallback(
        (product: WarehouseProduct): ProductForm => {
            const warehouseCategoryId =
                product?.warehouseCategory?.uuid ??
                product?.warehouseCategory?.id ??
                product?.warehouse_category?.uuid ??
                product?.warehouse_category?.id ??
                product?.warehouseCategoryId ??
                product?.warehouse_category_id;

            const legacyCategoryId =
                product?.category?.uuid ??
                product?.categoryId ??
                product?.category_id;

            const categoryId = warehouseCategoryId ?? legacyCategoryId;

            const matchedCategory = findOptionById(
                categoryOptions,
                categoryId,
            );

            // A category can be merged with a legacy option by label. When
            // the product itself references warehouseCategory, preserve that
            // source so subsequent saves continue using warehouseCategory.
            const selectedCategory =
                warehouseCategoryId && matchedCategory
                    ? {
                          ...matchedCategory,
                          categorySource: 'WAREHOUSE' as const,
                      }
                    : matchedCategory;

            const unit =
                product?.unit ??
                product?.unitOfMeasurement ??
                product?.unit_of_measurement;

            // `unitForPackage` is the warehouse-service wire name
            // (warehouse-service PR #244). Older/alternate shapes kept as
            // fallbacks.
            const unitOfPackage =
                product?.unitForPackage ??
                product?.unit_for_package ??
                product?.unitOfPackage ??
                product?.unit_of_package ??
                product?.packageUnit ??
                product?.package_unit;

            const vatCode =
                product?.vatCode ??
                product?.vat_code ??
                product?.vatGroup ??
                product?.vat_group;

            const status =
                product?.status?.value ??
                product?.status?.id ??
                product?.status;

            const ean = stringValue(product?.ean).trim();

            const useAsComponent = booleanValue(
                product?.useAsComponent ?? product?.use_as_component,
                false,
            );

            const includeComponents = booleanValue(
                product?.includeComponents ?? product?.include_components,
                false,
            );

            const isStandaloneProductRow =
                !useAsComponent && !includeComponents;

            const showMasterPackFieldsRow =
                !(!useAsComponent && includeComponents);

            const productWithoutEanRow = booleanValue(
                product?.productWithoutEan ?? product?.product_without_ean,
                !stringValue(product?.ean).trim(),
            );

            const showMasterPackEanRow =
                showMasterPackFieldsRow && !productWithoutEanRow;

            const packagingValue =
                product?.returnablePackaging ?? product?.returnable_packaging;

            const packagingUuid =
                packagingValue?.uuid ??
                product?.returnablePackagingUuid ??
                product?.returnable_packaging_uuid;

            const packagingOption = findOptionById(
                returnablePackagingOptions,
                packagingUuid,
            );

            const returnablePackagingEnabled =
                isStandaloneProductRow &&
                (
                    booleanValue(
                        product?.returnablePackagingEnabled ??
                            product?.returnable_packaging_enabled,
                        false,
                    ) ||
                    Boolean(packagingOption) ||
                    Boolean(
                        packagingValue &&
                        typeof packagingValue === 'object',
                    )
                );

            const returnablePackagingPrice =
                isStandaloneProductRow
                    ? numberOrNull(
                          product?.returnablePackagingPrice ??
                              product?.returnable_packaging_price ??
                              packagingValue?.price ??
                              packagingOption?.price,
                      )
                    : null;

            const storedPrice = numberOrNull(
                product?.price?.parsedValue ?? product?.price,
            );

            const currencyValue = stringValue(
                product?.currency?.code ??
                    product?.currency?.value ??
                    product?.currency?.id ??
                    product?.currency ??
                    DEFAULT_CURRENCY,
            ).trim() || DEFAULT_CURRENCY;

            return {
                systemId: stringValue(
                    product?.systemId ??
                        product?.system_id ??
                        product?.resourceSystemId ??
                        product?.resource_system_id,
                ),
                externalSystemId: stringValue(
                    product?.externalSystemId ??
                        product?.external_system_id ??
                        product?.external_id,
                ),

                productWithoutEan: booleanValue(
                    product?.productWithoutEan ?? product?.product_without_ean,
                    !ean,
                ),
                ean,

                name: stringValue(product?.name),
                useAsComponent,
                category: selectedCategory,
                includeComponents,

                weightVolume: numberOrNull(
                    product?.weightVolume ?? product?.weight_volume,
                ),
                unitOfPackage: showMasterPackFieldsRow
                    ? findOptionById(unitOptions, unitOfPackage)
                    : null,
                price: calculateBaseProductPrice(
                    storedPrice,
                    returnablePackagingEnabled,
                    returnablePackagingPrice,
                ),
                currency: {
                    id: currencyValue,
                    label: currencyValue,
                },
                returnablePackagingEnabled,
                returnablePackaging:
                    isStandaloneProductRow
                        ? packagingOption ??
                          (packagingValue?.uuid
                              ? {
                                    uuid: packagingValue.uuid,
                                    label: getI18FromName(
                                        packagingValue.name,
                                    ),
                                    originalName: packagingValue.name,
                                    price:
                                        packagingValue.price ??
                                        product?.returnablePackagingPrice ??
                                        product?.returnable_packaging_price ??
                                        null,
                                }
                              : null)
                        : null,
                returnablePackagingPrice,
                masterPackEan: showMasterPackEanRow
                    ? stringValue(
                          product?.eanMasterPack ??
                              product?.ean_master_pack ??
                              product?.masterPackEan ??
                              product?.master_pack_ean,
                      )
                    : '',

                unitOfMeasurement: findOptionById(unitOptions, unit),
                vatCode: findOptionById(vatCodeOptions, vatCode),
                minQuantity:
                    isStandaloneProductRow
                        ? numberOrNull(
                              product?.minQuantity ?? product?.min_quantity,
                          )
                        : null,
                maxQuantity:
                    isStandaloneProductRow
                        ? numberOrNull(
                              product?.maxQuantity ?? product?.max_quantity,
                          )
                        : null,
                unitsPerMasterPack: showMasterPackFieldsRow
                    ? numberOrNull(
                          product?.unitsPerMasterPack ??
                              product?.units_per_master_pack,
                      )
                    : null,

                status: findOptionById(statusOptions, status),
            };
        },
        [
            categoryOptions,
            findOptionById,
            getI18FromName,
            returnablePackagingOptions,
            statusOptions,
            unitOptions,
            vatCodeOptions,
        ],
    );

    const fetchUnitOptions = useCallback(() => {
        ProductService.getUnitOfMeasurementList()
            .then((units: string[]) => {
                if (!isMounted.current) return;

                setUnitOptions(
                    (units ?? []).map((unit) => ({
                        id: unit,
                        label: t(`products.unitOfMeasurement.${unit}`, unit),
                    })),
                );
            })
            .catch(() => {
                showErrorMessage(t('products.error.fetchUnitOfMeasurements'));
            });
    }, [showErrorMessage, t]);

    const fetchStatusOptions = useCallback(() => {
        ProductService.getStatusList()
            .then((statuses: string[]) => {
                if (!isMounted.current) return;

                setStatusOptions(
                    (statuses ?? []).map((status) => ({
                        id: status,
                        label: t(`ProductStatus.${status}`, status),
                    })),
                );
            })
            .catch(() => {
                showErrorMessage(t('products.error.fetchStatuses'));
            });
    }, [showErrorMessage, t]);

    const fetchVatCodeOptions = useCallback(() => {
        ProductService.getVatCodeList()
            .then((codes: string[]) => {
                if (!isMounted.current) return;

                setVatCodeOptions(
                    (codes ?? []).map((code) => ({
                        id: code,
                        label: t(`products.vatCodes.${code}`, code),
                    })),
                );
            })
            .catch(() => {
                showErrorMessage(t('products.error.fetchVatCodes'));
            });
    }, [showErrorMessage, t]);

    const fetchCategoryOptions = useCallback(async () => {
        const normalizeLabel = (value: string) =>
            value.trim().toLocaleLowerCase();

        const inferLegacyComponentFlag = (
            category: any,
        ): boolean | undefined => {
            const explicitComponent =
                category?.useAsComponent ??
                category?.use_as_component ??
                category?.isComponent ??
                category?.is_component;

            if (explicitComponent !== undefined) {
                return Boolean(explicitComponent);
            }

            const rawType = String(
                category?.type ??
                    category?.categoryType ??
                    category?.category_type ??
                    '',
            ).toUpperCase();

            if (rawType === 'COMPONENT') {
                return true;
            }

            if (rawType === 'PRODUCT') {
                return false;
            }

            return undefined;
        };

        const fetchAllWarehouseCategories = async (): Promise<any[]> => {
            const collected: any[] = [];
            let page = 1;
            let totalPages = 1;

            do {
                const response = await WarehouseProductCategoryService.getList(
                    new ListParams({
                        page,
                        size: 100,
                        sort: 'name:asc',
                    }),
                );

                collected.push(...(response?.content ?? []));

                totalPages = Math.max(
                    Number(
                        (response as any)?.total_pages ??
                            (response as any)?.totalPages ??
                            1,
                    ),
                    1,
                );

                page += 1;
            } while (page <= totalPages);

            return collected;
        };

        try {
            const [legacyResult, warehouseResult] = await Promise.allSettled([
                ProductService.getCategoryNames(),
                fetchAllWarehouseCategories(),
            ]);

            if (!isMounted.current) return;

            const legacyCategories =
                legacyResult.status === 'fulfilled'
                    ? (legacyResult.value ?? [])
                    : [];

            const warehouseCategories =
                warehouseResult.status === 'fulfilled'
                    ? (warehouseResult.value ?? [])
                    : [];

            if (
                legacyResult.status === 'rejected' &&
                warehouseResult.status === 'rejected'
            ) {
                showErrorMessage(t('products.error.fetchCategories'));
                setCategoryOptions([]);
                return;
            }

            if (legacyResult.status === 'rejected') {
                console.error(
                    'Failed to load legacy product categories:',
                    legacyResult.reason,
                );
            }

            if (warehouseResult.status === 'rejected') {
                console.error(
                    'Failed to load warehouse product categories:',
                    warehouseResult.reason,
                );
            }

            const warehouseOptions: Option[] = warehouseCategories
                .filter((category: any) => {
                    const type = String(category?.type ?? '').toUpperCase();

                    const status = String(
                        category?.status ?? 'ACTIVE',
                    ).toUpperCase();

                    return (
                        status !== 'INACTIVE' &&
                        status !== 'DELETED' &&
                        (type === 'PRODUCT' || type === 'COMPONENT')
                    );
                })
                .map((category: any): Option => {
                    const type = String(
                        category?.type ?? 'PRODUCT',
                    ).toUpperCase() as 'PRODUCT' | 'COMPONENT';

                    const warehouseCategoryId = stringValue(
                        category?.uuid ??
                            category?.id ??
                            category?.externalId ??
                            category?.external_id,
                    );

                    return {
                        id: warehouseCategoryId,
                        uuid: warehouseCategoryId,
                        warehouseCategoryId,
                        label: stringValue(category?.name),
                        originalName: category?.name,
                        component: type === 'COMPONENT',
                        categoryType: type,
                        categorySource: 'WAREHOUSE',
                    };
                })
                .filter(
                    (option: Option) =>
                        Boolean(option.uuid) && Boolean(option.label.trim()),
                );

            const mergedOptions = [...warehouseOptions];

            for (const category of legacyCategories as any[]) {
                const legacyCategoryId = stringValue(
                    category?.uuid ??
                        category?.id ??
                        category?.externalId ??
                        category?.external_id,
                );

                const label = getI18FromName(category?.name);
                const component = inferLegacyComponentFlag(category);

                if (!legacyCategoryId || !label?.trim()) {
                    continue;
                }

                const normalizedLabel = normalizeLabel(label);

                const matchingWarehouseOption = mergedOptions.find((option) => {
                    if (option.categorySource !== 'WAREHOUSE') {
                        return false;
                    }

                    if (normalizeLabel(option.label) !== normalizedLabel) {
                        return false;
                    }

                    return (
                        component === undefined ||
                        option.component === component
                    );
                });

                if (matchingWarehouseOption) {
                    matchingWarehouseOption.legacyCategoryId = legacyCategoryId;
                    matchingWarehouseOption.categorySource = 'MERGED';
                    continue;
                }

                mergedOptions.push({
                    id: legacyCategoryId,
                    uuid: legacyCategoryId,
                    legacyCategoryId,
                    label,
                    originalName: category?.name,
                    component,
                    categoryType:
                        component === true
                            ? 'COMPONENT'
                            : component === false
                              ? 'PRODUCT'
                              : undefined,
                    categorySource: 'LEGACY',
                });
            }

            const deduplicated = new Map<string, Option>();

            for (const option of mergedOptions) {
                const idKey =
                    option.warehouseCategoryId ??
                    option.legacyCategoryId ??
                    option.uuid ??
                    String(option.id ?? '');

                const fallbackKey = [
                    normalizeLabel(option.label),
                    option.component === true
                        ? 'COMPONENT'
                        : option.component === false
                          ? 'PRODUCT'
                          : 'UNTYPED',
                ].join('::');

                const key = idKey ? `id::${idKey}` : `label::${fallbackKey}`;

                if (!deduplicated.has(key)) {
                    deduplicated.set(key, option);
                }
            }

            setCategoryOptions(
                Array.from(deduplicated.values()).sort((left, right) =>
                    left.label.localeCompare(right.label, undefined, {
                        sensitivity: 'base',
                    }),
                ),
            );
        } catch (error) {
            console.error('Failed to load merged product categories:', error);

            if (isMounted.current) {
                showErrorMessage(t('products.error.fetchCategories'));
            }
        }
    }, [getI18FromName, showErrorMessage, t]);

    const fetchReturnablePackagingOptions = useCallback(() => {
        ReturnablePackagingService.list()
            .then((items: any[]) => {
                if (!isMounted.current) return;

                setReturnablePackagingOptions(
                    (items ?? [])
                        .map((item) => ({
                            uuid: item?.packaging?.uuid,
                            label: getI18FromName(item?.packaging?.name),
                            originalName: item?.packaging?.name,
                            price: numberOrNull(item?.price),
                        }))
                        .filter((option) => option.uuid && option.label),
                );
            })
            .catch(() => {
                showErrorMessage(t('products.error.fetchReturnablePackaging'));
            });
    }, [getI18FromName, showErrorMessage, t]);

    useEffect(() => {
        isMounted.current = true;

        fetchUnitOptions();
        fetchStatusOptions();
        fetchVatCodeOptions();
        fetchCategoryOptions();
        fetchReturnablePackagingOptions();

        return () => {
            isMounted.current = false;

            if (eanDebounceRef.current) {
                clearTimeout(eanDebounceRef.current);
            }
        };
    }, [
        fetchCategoryOptions,
        fetchReturnablePackagingOptions,
        fetchStatusOptions,
        fetchUnitOptions,
        fetchVatCodeOptions,
    ]);

    const fetchProduct = useCallback(async () => {
        if (!externalId) return;

        try {
            setIsLoading(true);

            const product = await ProductService.get(externalId);

            if (!isMounted.current) return;

            setLoadedProduct(product as WarehouseProduct);
        } catch (error) {
            console.error('Failed to load warehouse product:', error);
            showErrorMessage(t('products.error.fetch'));
        } finally {
            if (isMounted.current) {
                setIsLoading(false);
            }
        }
    }, [externalId, showErrorMessage, t]);

    useEffect(() => {
        if (!visible) return;

        if (viewMode === 'EDIT' && externalId) {
            fetchProduct();
            return;
        }

        if (viewMode === 'NEW' && !duplicatedProductData) {
            resetForm();

            setForm((previous) => ({
                ...previous,
                name: initialName?.trim() ?? '',
                productWithoutEan: initialProductWithoutEan,
                ean: initialProductWithoutEan
                    ? ''
                    : (initialEanCode ?? ''),
                useAsComponent: initialUseAsComponent,
                includeComponents: false,
            }));
        }
    }, [
        duplicatedProductData,
        externalId,
        fetchProduct,
        initialEanCode,
        initialName,
        initialProductWithoutEan,
        initialUseAsComponent,
        resetForm,
        viewMode,
        visible,
    ]);

    useEffect(() => {
        if (!visible || viewMode !== 'EDIT' || !loadedProduct) return;

        setForm(mapProductToForm(loadedProduct));
        focusNameInput();
    }, [focusNameInput, loadedProduct, mapProductToForm, viewMode, visible]);

    useEffect(() => {
        if (!visible || viewMode !== 'NEW' || !duplicatedProductData) {
            return;
        }

        const duplicatedForm = mapProductToForm(duplicatedProductData);

        setLoadedProduct(null);
        setForm({
            ...duplicatedForm,
            systemId: '',
            externalSystemId: '',
            ean: '',
            masterPackEan: '',
        });
        focusNameInput();
    }, [
        duplicatedProductData,
        focusNameInput,
        mapProductToForm,
        viewMode,
        visible,
    ]);

    useEffect(() => {
        if (visible && viewMode === 'NEW' && statusOptions.length > 0) {
            const active = statusOptions.find(
                (status) => status.id === 'ACTIVE',
            );

            if (active) {
                setForm((previous) => ({
                    ...previous,
                    status: previous.status ?? active,
                }));
            }
        }
    }, [statusOptions, viewMode, visible]);

    useEffect(() => {
        const hasPhoto = Boolean(
            loadedProduct?.hasPhoto ?? loadedProduct?.has_photo,
        );
        const ean = stringValue(loadedProduct?.ean).trim();

        if (!visible || !hasPhoto || !ean) {
            setPhotoUrl(null);
            setIsPhotoLoading(false);
            return;
        }

        const url = ProductService.getPhotoUrl({ ean });
        setPhotoUrl(url);
        setIsPhotoLoading(Boolean(url));
    }, [loadedProduct, visible]);

    useEffect(() => {
        if (!visible || form.productWithoutEan) {
            setIsEanAvailable(true);
            setIsCheckingEan(false);
            return;
        }

        const rawEan = form.ean.trim();

        if (!rawEan) {
            setIsEanAvailable(true);
            setIsCheckingEan(false);
            return;
        }

        const loadedEan = stringValue(loadedProduct?.ean).trim();

        if (viewMode === 'EDIT' && loadedEan && rawEan === loadedEan) {
            setIsEanAvailable(true);
            setIsCheckingEan(false);
            return;
        }

        if (eanDebounceRef.current) {
            clearTimeout(eanDebounceRef.current);
        }

        setIsCheckingEan(true);

        eanDebounceRef.current = setTimeout(async () => {
            try {
                const available =
                    await ProductService.checkEanAvailability(rawEan);

                if (isMounted.current) {
                    setIsEanAvailable(Boolean(available));
                }
            } catch (error) {
                console.error('Failed to check EAN availability:', error);

                if (isMounted.current) {
                    setIsEanAvailable(true);
                }
            } finally {
                if (isMounted.current) {
                    setIsCheckingEan(false);
                }
            }
        }, 400);

        return () => {
            if (eanDebounceRef.current) {
                clearTimeout(eanDebounceRef.current);
            }
        };
    }, [form.ean, form.productWithoutEan, loadedProduct, viewMode, visible]);

    const handleChange = (event: any, field: keyof ProductForm) => {
        const value =
            event?.value !== undefined ? event.value : event?.target?.value;

        setForm((previous) => ({
            ...previous,
            [field]: value,
        }));
    };

    /**
     * simple-react-validator keeps previously rendered fields in its registry.
     * When a conditional branch is hidden, a required field from that branch
     * can remain invalid even though it is no longer visible.
     *
     * Purge the registry and remount the current form branch so only currently
     * rendered inputs participate in validation.
     */
    const resetConditionalValidation = (remount = true) => {
        validator.hideMessages();

        const validatorWithPurge = validator as typeof validator & {
            purgeFields?: () => void;
        };

        validatorWithPurge.purgeFields?.();

        if (remount) {
            setMainKey((previous) => previous + 1);
        }
    };

    const handleProductWithoutEanChange = (event: any) => {
        const checked = Boolean(
            event?.value ?? event?.checked ?? event?.target?.checked,
        );

        if (eanDebounceRef.current) {
            clearTimeout(eanDebounceRef.current);
        }

        setIsEanAvailable(true);
        setIsCheckingEan(false);

        setForm((previous) => ({
            ...previous,
            productWithoutEan: checked,
            ...(checked
                ? {
                      ean: '',
                      masterPackEan: '',
                  }
                : {}),
        }));

        resetConditionalValidation();
    };

    const handleUseAsComponentChange = (event: any) => {
        const nextUseAsComponent = Boolean(event?.value?.id);

        setForm((previous) => {
            const nextIncludeComponents = nextUseAsComponent
                ? false
                : previous.includeComponents;

            const nextShowMasterPackFields = !(
                !nextUseAsComponent && nextIncludeComponents
            );

            return {
                ...previous,
                useAsComponent: nextUseAsComponent,
                includeComponents: nextIncludeComponents,
                category:
                    previous.category?.categorySource !== 'WAREHOUSE' &&
                    previous.category?.categorySource !== 'MERGED' &&
                    previous.category?.component !== undefined &&
                    previous.category.component !== nextUseAsComponent
                        ? null
                        : previous.category,
                // Switching into "contains components" clears master-pack
                // fields; switching out of it (or into "component") keeps them.
                ...(!nextShowMasterPackFields
                    ? {
                          unitOfPackage: null,
                          unitsPerMasterPack: null,
                          masterPackEan: '',
                      }
                    : {}),
            };
        });

        resetConditionalValidation();
    };

    const handleIncludeComponentsChange = (event: any) => {
        const nextIncludeComponents = Boolean(event?.value?.id);

        setForm((previous) => ({
            ...previous,
            includeComponents: nextIncludeComponents,
            ...(nextIncludeComponents
                ? {
                      minQuantity: null,
                      maxQuantity: null,
                      returnablePackagingEnabled: false,
                      returnablePackaging: null,
                      returnablePackagingPrice: null,
                      unitOfPackage: null,
                      unitsPerMasterPack: null,
                      masterPackEan: '',
                  }
                : {}),
        }));

        resetConditionalValidation();
    };

    const handleReturnablePackagingChange = (event: any) => {
        const checked = Boolean(
            event?.value ?? event?.checked ?? event?.target?.checked,
        );

        setForm((previous) => ({
            ...previous,
            returnablePackagingEnabled: checked,
            ...(!checked
                ? {
                      returnablePackaging: null,
                      returnablePackagingPrice: null,
                  }
                : {}),
        }));

        // Keep the current sidebar scroll position when the conditional
        // returnable-packaging fields are shown or hidden.
        resetConditionalValidation(false);
    };

    const handleOnShow = () => {
        validator.hideMessages();

        if (viewMode !== 'NEW' || duplicatedProductData) {
            return;
        }

        resetForm();

        const active = statusOptions.find((status) => status.id === 'ACTIVE');

        setForm((previous) => ({
            ...previous,
            name: initialName?.trim() ?? '',
            productWithoutEan: initialProductWithoutEan,
            ean: initialProductWithoutEan
                ? ''
                : (initialEanCode ?? ''),
            useAsComponent: initialUseAsComponent,
            includeComponents: false,
            status: active ?? previous.status,
        }));
    };

    const handleOnHide = () => {
        validator.hideMessages();
        onHide?.();
    };

    const buildProductRequest = (): InventoryProductRequest => {
        const normalizedEan = form.ean.trim();

        const finalProductPrice = calculateFinalProductPrice(
            form.price,
            isStandaloneProduct &&
                form.returnablePackagingEnabled,
            isStandaloneProduct
                ? form.returnablePackagingPrice
                : null,
        );

        const legacyCategoryUuid =
            form.category?.categorySource !== 'WAREHOUSE'
                ? (form.category?.legacyCategoryId ?? form.category?.uuid)
                : undefined;

        const warehouseCategoryUuid =
            form.category?.categorySource === 'WAREHOUSE'
                ? (form.category?.warehouseCategoryId ?? form.category?.uuid)
                : undefined;

        const returnablePackaging =
            isStandaloneProduct &&
            form.returnablePackagingEnabled &&
            form.returnablePackaging?.uuid
                ? {
                      uuid: form.returnablePackaging.uuid,
                      name: form.returnablePackaging.originalName ?? {
                          name: form.returnablePackaging.label,
                          namePl: form.returnablePackaging.label,
                      },
                  }
                : null;

        // Master-pack fields: omit the key entirely when the section is
        // hidden (contains-components branch) so the backend's
        // "omitted -> leave unchanged" contract applies and nothing is
        // unintentionally sent (see #841 acceptance criteria). When
        // visible, send the value, or an explicit "" to clear it.
        const normalizedMasterPackEan = form.masterPackEan.trim();

        return {
            externalSystemId: optionalString(form.externalSystemId),
            name: form.name.trim(),
            ean: form.productWithoutEan ? null : normalizedEan || null,
            category: legacyCategoryUuid
                ? {
                      uuid: legacyCategoryUuid,
                  }
                : undefined,
            warehouseCategory: warehouseCategoryUuid
                ? {
                      uuid: warehouseCategoryUuid,
                  }
                : undefined,
            weightVolume: form.weightVolume ?? undefined,
            unitOfMeasurement:
                typeof form.unitOfMeasurement?.id === 'string'
                    ? form.unitOfMeasurement.id
                    : undefined,
            minQuantity: isStandaloneProduct
                ? (form.minQuantity ?? undefined)
                : undefined,
            maxQuantity: isStandaloneProduct
                ? (form.maxQuantity ?? undefined)
                : undefined,
            price: finalProductPrice ?? undefined,
            currency:
                typeof form.currency?.id === 'string'
                    ? form.currency.id
                    : DEFAULT_CURRENCY,
            vatCode:
                typeof form.vatCode?.id === 'string'
                    ? form.vatCode.id
                    : undefined,
            status:
                typeof form.status?.id === 'string'
                    ? form.status.id
                    : undefined,
            returnablePackaging,
            useAsComponent: form.useAsComponent,
            includeComponents: form.useAsComponent
                ? false
                : form.includeComponents,

            unitForPackage: showMasterPackFields
                ? (typeof form.unitOfPackage?.id === 'string'
                      ? form.unitOfPackage.id
                      : '')
                : undefined,
            // Section hidden entirely (contains-components) -> omit the key
            // so the backend's "omitted = unchanged" rule applies. Section
            // visible but "Product without EAN" is on -> explicitly clear,
            // since a master pack EAN can't be kept on a product with no EAN.
            eanMasterPack: showMasterPackFields
                ? (showMasterPackEan ? normalizedMasterPackEan || '' : '')
                : undefined,
            unitsPerMasterPack: showMasterPackFields
                ? (form.unitsPerMasterPack ?? null)
                : undefined,
        };
    };

    const buildUiOnlySnapshot = () => {
        const finalProductPrice = calculateFinalProductPrice(
            form.price,
            isStandaloneProduct &&
                form.returnablePackagingEnabled,
            isStandaloneProduct
                ? form.returnablePackagingPrice
                : null,
        );

        return {
            productWithoutEan: form.productWithoutEan,
            weightVolume: form.weightVolume,
            unitOfPackage: showMasterPackFields
                ? (typeof form.unitOfPackage?.id === 'string'
                      ? form.unitOfPackage.id
                      : null)
                : null,
            price: finalProductPrice,
            currency:
                typeof form.currency?.id === 'string'
                    ? form.currency.id
                    : DEFAULT_CURRENCY,
            returnablePackagingEnabled:
                isStandaloneProduct &&
                form.returnablePackagingEnabled,
            returnablePackaging:
                isStandaloneProduct
                    ? form.returnablePackaging
                    : null,
            returnablePackagingPrice:
                isStandaloneProduct
                    ? form.returnablePackagingPrice
                    : null,
            masterPackEan: showMasterPackEan
                ? form.masterPackEan.trim() || null
                : null,
            unitsPerMasterPack: showMasterPackFields
                ? form.unitsPerMasterPack
                : null,
        };
    };

    const handleSave = async () => {
        if (!form.productWithoutEan && !isEanAvailable) {
            showErrorMessage(t('products.eanWarning.message'));
            return;
        }

        if (!validator.allValid()) {
            validator.showMessages();
            setMainKey((previous) => previous + 1);
            showErrorMessage(t('common.validationError'));
            return;
        }

        setIsSaving(true);

        try {
            if (viewMode === 'NEW') {
                const createdProduct = await ProductService.create(
                    buildProductRequest(),
                );

                showSuccessMessage(t('products.success.create'));
                handleOnHide();
                onSuccess?.({
                    ...createdProduct,
                    ...buildUiOnlySnapshot(),
                });
                return;
            }

            if (!externalId) {
                throw new Error('Product externalId is required for update.');
            }

            const previousStatus = stringValue(
                loadedProduct?.status?.value ??
                    loadedProduct?.status?.id ??
                    loadedProduct?.status,
            );

            const nextStatus =
                typeof form.status?.id === 'string'
                    ? form.status.id
                    : undefined;

            const statusChanged =
                Boolean(nextStatus) && previousStatus !== nextStatus;

            const statusChangedToInactive =
                statusChanged && nextStatus === 'INACTIVE';

            const updatedProduct = await ProductService.update(
                externalId,
                buildProductRequest(),
            );

            showSuccessMessage(t('products.success.update'));

            if (statusChangedToInactive && onStatusChangedToInactive) {
                try {
                    const exists =
                        await PlanogramService.isProductInPlanograms(
                            externalId,
                        );

                    if (exists) {
                        onStatusChangedToInactive({
                            externalId,
                            productName:
                                form.name || stringValue(loadedProduct?.name),
                        });
                        return;
                    }
                } catch (error) {
                    console.error(
                        'Failed to check planogram after product deactivation:',
                        error,
                    );
                }
            }

            handleOnHide();
            onSuccess?.({
                ...updatedProduct,
                ...buildUiOnlySnapshot(),
            });
        } catch (error) {
            console.error('Inventory product save failed:', error);
            showErrorMessage(t('products.error.save'));
        } finally {
            if (isMounted.current) {
                setIsSaving(false);
            }
        }
    };

    const eanWarningActive = Boolean(
        !form.productWithoutEan && !isEanAvailable && form.ean.trim(),
    );

    const title =
        viewMode === 'EDIT' ? t('products.edit.title') : t('products.addNew');

    const commonInputTextProps = {
        leftIcon: null,
        inputClassName: '',
    };

    const productPrice = form.price ?? 0;
    const returnablePackagingPrice = form.returnablePackagingPrice ?? 0;
    const finalCustomerPrice =
        calculateFinalProductPrice(
            form.price,
            isStandaloneProduct &&
                form.returnablePackagingEnabled,
            isStandaloneProduct
                ? form.returnablePackagingPrice
                : null,
        ) ?? 0;

    const selectedCurrency =
        typeof form.currency?.id === 'string'
            ? form.currency.id
            : DEFAULT_CURRENCY;

    const formatPrice = (value: number) =>
        `${value.toFixed(2).replace('.', ',')} ${selectedCurrency}`;

    return (
        <DetailsSidebar
            className="product-sidebar"
            title={title}
            renderTitlePrefix={() => {
                if (!photoUrl) return null;

                return (
                    <div className="photo-preview-container">
                        {isPhotoLoading && <LoadingSpinner />}

                        <img
                            src={photoUrl}
                            alt={t('products.productPhoto')}
                            className="photo-preview"
                            style={{
                                display: isPhotoLoading ? 'none' : 'block',
                            }}
                            onLoad={() => setIsPhotoLoading(false)}
                            onError={() => {
                                setIsPhotoLoading(false);
                                setPhotoUrl(null);
                            }}
                        />
                    </div>
                );
            }}
            visible={visible}
            onHide={handleOnHide}
            onCancel={handleOnHide}
            onShow={handleOnShow}
            onSave={handleSave}
            contentKey={mainKey}
            isSaving={isSaving}
            isLoading={isLoading}
        >
            <div className="form-section">
                <div className="grid">
                    <div className="col">
                        <InputText
                            id="systemId"
                            label={t('products.systemId')}
                            value={form.systemId}
                            disabled
                            variant="filled"
                            validator={null}
                            validations=""
                            {...commonInputTextProps}
                        />
                    </div>

                    <div className="col">
                        <InputText
                            id="externalSystemId"
                            label={t(
                                'products.externalSystemId',
                                'ID external',
                            )}
                            value={form.externalSystemId}
                            onChange={(event: any) =>
                                handleChange(event, 'externalSystemId')
                            }
                            validator={validator}
                            validations="not_required|max:50"
                            {...commonInputTextProps}
                        />
                    </div>
                </div>

                <div className="flex align-items-center gap-3 mb-3">
                    <InputSwitch
                        id="productWithoutEan"
                        className=""
                        label={t(
                            'products.productWithoutEan',
                            'Product without EAN',
                        )}
                        checked={form.productWithoutEan}
                        onChange={handleProductWithoutEanChange}
                    />
                </div>

                {!form.productWithoutEan && (
                    <>
                        <label
                            htmlFor="text-ean"
                            style={{
                                color: eanWarningActive
                                    ? 'var(--orange-500)'
                                    : 'var(--main-dark-purple)',
                                textAlign: 'center',
                                fontFamily: 'Inter-Medium, sans-serif',
                                fontSize:
                                    'var(--titles-secondary-title-font-size)',
                                lineHeight:
                                    'var(--titles-secondary-title-line-height)',
                                letterSpacing:
                                    'var(--titles-secondary-title-letter-spacing)',
                                fontWeight:
                                    'var(--titles-secondary-title-font-weight)',
                                textTransform: 'uppercase',
                                opacity: 0.6,
                                position: 'relative',
                                marginBottom: '6px',
                                display: 'inline-block',
                            }}
                        >
                            {t('products.ean')}
                        </label>

                        <InputText
                            id="ean"
                            label={t('products.ean')}
                            showLabel={false}
                            value={form.ean}
                            onChange={(event: any) =>
                                handleChange(event, 'ean')
                            }
                            validator={validator}
                            validations="required"
                            keyfilter="int"
                            pattern="\\d*"
                            inputMode="numeric"
                            placeholder="1234567890123"
                            maxLength={64}
                            className="w-full"
                            style={{
                                border: eanWarningActive
                                    ? '1px solid var(--orange-500)'
                                    : '',
                                color: eanWarningActive
                                    ? 'var(--orange-500)'
                                    : undefined,
                            }}
                            {...commonInputTextProps}
                        />

                        {eanWarningActive && (
                            <div className="flex align-items-center gap-1 text-xs text-orange-500 pt-2 pb-4">
                                <WarningIcon className="text-orange-500" />
                                <span className="font-semibold">
                                    {t('products.eanWarning.warning')}{' '}
                                </span>
                                {t('products.eanWarning.message')}
                            </div>
                        )}

                        {isCheckingEan && form.ean.trim() && (
                            <div className="text-color-secondary text-xs pt-2 pb-4">
                                <i className="pi pi-spin pi-spinner mr-1" />
                                {t('products.checkingEan')}
                            </div>
                        )}
                    </>
                )}

                <InputText
                    id="name"
                    label={t('products.name')}
                    value={form.name}
                    onChange={(event: any) => handleChange(event, 'name')}
                    validator={validator}
                    validations="required"
                    className="w-full mb-3"
                    {...commonInputTextProps}
                />

                <Dropdown
                    id="useAsComponent"
                    label={t('products.useAsComponent', 'Use as component')}
                    options={yesNoOptions}
                    value={selectedUseAsComponent}
                    dataKey="id"
                    optionLabel="label"
                    onChange={handleUseAsComponentChange}
                    validator={null}
                    validations=""
                    placeholder={t('common.select')}
                    className="w-full mb-3"
                />

                <Dropdown
                    id="category"
                    label={t('products.category')}
                    options={visibleCategoryOptions}
                    value={form.category}
                    dataKey="uuid"
                    optionLabel="label"
                    onChange={(event: any) => handleChange(event, 'category')}
                    validator={validator}
                    validations="required"
                    className="w-full mb-3"
                    placeholder={t('common.select')}
                />

                {!form.useAsComponent && (
                    <Dropdown
                        id="includeComponents"
                        label={t(
                            'products.includeComponents',
                            'Include components',
                        )}
                        options={yesNoOptions}
                        value={selectedIncludeComponents}
                        dataKey="id"
                        optionLabel="label"
                        onChange={handleIncludeComponentsChange}
                        validator={null}
                        validations=""
                        placeholder={t('common.select')}
                        className="w-full mb-3"
                    />
                )}

                {!form.useAsComponent ? (
                    <>
                        <div className="grid">
                            <div className="col">
                                <InputNumber
                                    id="weightVolume"
                                    label={t(
                                        'products.weightVolume',
                                        'Weight/volume',
                                    )}
                                    value={form.weightVolume}
                                    onChange={(event: any) =>
                                        handleChange(event, 'weightVolume')
                                    }
                                    validator={null}
                                    validations=""
                                    mode="decimal"
                                    min={0}
                                    minFractionDigits={0}
                                    maxFractionDigits={3}
                                    useGrouping={false}
                                />
                            </div>

                            <div className="col">
                                <Dropdown
                                    id="unitOfMeasurement"
                                    label={t('products.udm')}
                                    options={unitOptions}
                                    value={form.unitOfMeasurement}
                                    dataKey="id"
                                    optionLabel="label"
                                    onChange={(event: any) =>
                                        handleChange(event, 'unitOfMeasurement')
                                    }
                                    validator={validator}
                                    validations="required"
                                    placeholder={t('common.select')}
                                />
                            </div>
                        </div>

                        {isStandaloneProduct && (
                            <div className="grid">
                                <div className="col">
                                    <InputNumber
                                        id="minQuantity"
                                        label={t('products.minQuantity')}
                                        value={form.minQuantity}
                                        onChange={(event: any) =>
                                            handleChange(
                                                event,
                                                'minQuantity',
                                            )
                                        }
                                        validator={validator}
                                        validations="required"
                                        min={0}
                                    />
                                </div>

                                <div className="col">
                                    <InputNumber
                                        id="maxQuantity"
                                        label={t('products.maxQuantity')}
                                        value={form.maxQuantity}
                                        onChange={(event: any) =>
                                            handleChange(
                                                event,
                                                'maxQuantity',
                                            )
                                        }
                                        validator={validator}
                                        validations="required"
                                        min={0}
                                    />
                                </div>
                            </div>
                        )}

                        <div className="grid">
                            <div className="col">
                                <InputNumber
                                    id="price"
                                    label={t('products.price')}
                                    value={form.price}
                                    onChange={(event: any) =>
                                        handleChange(event, 'price')
                                    }
                                    validator={null}
                                    validations=""
                                    mode="decimal"
                                    minFractionDigits={2}
                                    maxFractionDigits={2}
                                    useGrouping
                                    min={0}
                                    maxLength={10}
                                />
                            </div>

                            <div className="col">
                                <Dropdown
                                    id="currency"
                                    label={t('products.currency', 'Currency')}
                                    options={currencyOptions}
                                    value={form.currency}
                                    dataKey="id"
                                    optionLabel="label"
                                    onChange={(event: any) =>
                                        handleChange(event, 'currency')
                                    }
                                    validator={validator}
                                    validations="required"
                                    placeholder={t('common.select')}
                                />
                            </div>
                        </div>

                        <Dropdown
                            id="vatCode"
                            label={t('products.vatCode', 'VAT code')}
                            options={vatCodeOptions}
                            value={form.vatCode}
                            dataKey="id"
                            optionLabel="label"
                            onChange={(event: any) =>
                                handleChange(event, 'vatCode')
                            }
                            validator={validator}
                            validations="required"
                            placeholder={t('common.select')}
                            className="w-full mb-3"
                        />

                        {isStandaloneProduct && (
                            <>
                        <div className="separator mb-3" />

                        <div className="flex align-items-center gap-3 mb-3">
                            <InputSwitch
                                id="returnablePackagingEnabled"
                                className=""
                                label={t(
                                    'products.returnablePackaging.toggle',
                                    'Returnable packaging',
                                )}
                                checked={form.returnablePackagingEnabled}
                                onChange={handleReturnablePackagingChange}
                            />
                        </div>

                        {form.returnablePackagingEnabled && (
                            <>
                                <div className="returnable-packaging-panel">
                                    <Dropdown
                                        id="returnablePackaging"
                                        label={t(
                                            'products.returnablePackaging.name',
                                            'Name',
                                        )}
                                        options={returnablePackagingOptions}
                                        value={form.returnablePackaging}
                                        dataKey="uuid"
                                        optionLabel="label"
                                        onChange={(event: any) => {
                                            const selected = event.value;

                                            setForm((previous) => ({
                                                ...previous,
                                                returnablePackaging: selected,
                                                returnablePackagingPrice:
                                                    selected?.price ?? null,
                                            }));
                                        }}
                                        validator={validator}
                                        validations="required"
                                        placeholder={t('common.select')}
                                        showClear
                                        className="returnable-packaging-panel__control"
                                    />
                                    <Dropdown
                                        id="returnablePackagingPrice"
                                        label={t(
                                            'products.returnablePackaging.price',
                                            'Price',
                                        )}
                                        options={
                                            returnablePackagingPriceOptions
                                        }
                                        value={selectedReturnablePackagingPrice}
                                        dataKey="id"
                                        optionLabel="label"
                                        onChange={(event: any) =>
                                            setForm((previous) => ({
                                                ...previous,
                                                returnablePackagingPrice:
                                                    numberOrNull(
                                                        event?.value?.id,
                                                    ),
                                            }))
                                        }
                                        validator={null}
                                        validations=""
                                        placeholder={t('common.select')}
                                        className="returnable-packaging-panel__control"
                                    />

                                    <div className="returnable-packaging-panel__summary">
                                        <div className="returnable-packaging-panel__summary-details">
                                            <span className="returnable-packaging-panel__summary-title">
                                                {t(
                                                    'products.returnablePackaging.finalPrice',
                                                    'Final price displayed for customer',
                                                )}
                                            </span>

                                            <span className="returnable-packaging-panel__calculation">
                                                {formatPrice(productPrice)} +{' '}
                                                {formatPrice(
                                                    returnablePackagingPrice,
                                                )}
                                            </span>
                                        </div>

                                        <strong className="returnable-packaging-panel__total">
                                            {formatPrice(finalCustomerPrice)}
                                        </strong>
                                    </div>

                                    <p className="returnable-packaging-panel__helper">
                                        {t(
                                            'products.returnablePackaging.finalPriceHelper',
                                            'The total price, including the deposit, will be displayed to the customer on the machine.',
                                        )}
                                    </p>
                                </div>

                                <div className="separator mb-3" />
                            </>
                        )}
                            </>
                        )}
                    </>
                ) : (
                    <>
                        <div className="grid">
                            <div className="col">
                                <Dropdown
                                    id="unitOfMeasurement"
                                    label={t('products.udm')}
                                    options={unitOptions}
                                    value={form.unitOfMeasurement}
                                    dataKey="id"
                                    optionLabel="label"
                                    onChange={(event: any) =>
                                        handleChange(event, 'unitOfMeasurement')
                                    }
                                    validator={validator}
                                    validations="required"
                                    placeholder={t('common.select')}
                                />
                            </div>

                            <div className="col">
                                <Dropdown
                                    id="unitOfPackage"
                                    label={t(
                                        'products.unitOfPackage',
                                        'Unit of package',
                                    )}
                                    options={unitOptions}
                                    value={form.unitOfPackage}
                                    dataKey="id"
                                    optionLabel="label"
                                    onChange={(event: any) =>
                                        handleChange(event, 'unitOfPackage')
                                    }
                                    validator={null}
                                    validations=""
                                    placeholder={t('common.select')}
                                />
                            </div>
                        </div>

                        <Dropdown
                            id="vatCode"
                            label={t('products.vatCode', 'VAT code')}
                            options={vatCodeOptions}
                            value={form.vatCode}
                            dataKey="id"
                            optionLabel="label"
                            onChange={(event: any) =>
                                handleChange(event, 'vatCode')
                            }
                            validator={validator}
                            validations="required"
                            placeholder={t('common.select')}
                            className="w-full mb-3"
                        />
                    </>
                )}

                {showMasterPackFields && (
                    <InputNumber
                        id="unitsPerMasterPack"
                        label={t(
                            'products.unitsPerMasterPack',
                            'Units per master pack',
                        )}
                        value={form.unitsPerMasterPack}
                        onChange={(event: any) =>
                            handleChange(event, 'unitsPerMasterPack')
                        }
                        validator={null}
                        validations=""
                        min={1}
                        useGrouping={false}
                        className="w-full mb-3"
                    />
                )}

                {showMasterPackEan && (
                    <InputText
                        id="masterPackEan"
                        label={t(
                            'products.masterPackEan',
                            'Master pack EAN',
                        )}
                        value={form.masterPackEan}
                        onChange={(event: any) =>
                            handleChange(event, 'masterPackEan')
                        }
                        validator={validator}
                        validations="not_required|max:64"
                        keyfilter="int"
                        pattern="\\d*"
                        inputMode="numeric"
                        maxLength={64}
                        className="w-full mb-3"
                        {...commonInputTextProps}
                    />
                )}

                <Dropdown
                    id="status"
                    label={t('products.status')}
                    options={statusOptions}
                    value={form.status}
                    dataKey="id"
                    optionLabel="label"
                    onChange={(event: any) => handleChange(event, 'status')}
                    validator={validator}
                    validations="required"
                    placeholder={t('common.select')}
                />
            </div>
        </DetailsSidebar>
    );
};

export default ProductSidebar;
